/**
 * Picker for "Campaign as offer" — same shape as the Offer picker but lists
 * Binom campaigns and emits items with the `campaign:<id>` id-prefix the
 * server-side merger maps to Binom's campaign-as-offer convention.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, useMemo, useState } from 'react';

import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/layout/Drawer';
import { Button } from '@/components/ui/layout/Button';
import { Input } from '@/components/ui/form/input/Input';
import { useBinomCampaigns } from '@/hooks/binom';

import type { BinomDraftItem } from '../../binom-flow-draft.types';

export interface BinomCampaignAsOfferPickerProps {
  open: boolean;
  onClose: () => void;
  excludeIds: string[];
  onAttach: (item: BinomDraftItem) => void;
}

export function campaignAsOfferItem(c: {
  id: string;
  name: string;
}): BinomDraftItem {
  return {
    id: `campaign:${c.id}`,
    name: c.name || `Campaign ${c.id}`,
    weight: 100,
    originalWeight: 100,
    active: true,
    originalActive: true,
  };
}

const BinomCampaignAsOfferPicker: FC<BinomCampaignAsOfferPickerProps> = ({
  open,
  onClose,
  excludeIds,
  onAttach,
}) => {
  const [search, setSearch] = useState('');
  const campaignsQuery = useBinomCampaigns();
  const lowered = search.trim().toLowerCase();

  const rows = useMemo(() => {
    const all = Array.isArray(campaignsQuery.data) ? campaignsQuery.data : [];
    return all
      .filter((c) => !excludeIds.includes(`campaign:${c.id}`))
      .filter((c) => !lowered || c.name.toLowerCase().includes(lowered))
      .slice(0, 200);
  }, [campaignsQuery.data, excludeIds, lowered]);

  const close = () => {
    setSearch('');
    onClose();
  };

  return (
    <Drawer open={open} onOpenChange={(o) => !o && close()}>
      <DrawerContent variant="center" width="w-[42rem]">
        <DrawerHeader>
          <DrawerTitle>Choose campaign as offer</DrawerTitle>
        </DrawerHeader>
        <DrawerBody>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search…"
            className="mb-2 h-8 text-xs"
          />
          <div className="max-h-[24rem] overflow-y-auto rounded-md border border-border/40">
            {campaignsQuery.isLoading ? (
              <p className="p-3 text-xs text-muted-foreground">Loading…</p>
            ) : rows.length === 0 ? (
              <p className="p-3 text-xs text-muted-foreground">No matches.</p>
            ) : (
              <ul className="divide-y divide-border/30">
                {rows.map((c) => (
                  <li
                    key={c.id}
                    className="flex items-center justify-between gap-2 px-2 py-1.5 text-xs hover:bg-muted/20"
                  >
                    <span className="truncate">
                      <span className="text-muted-foreground tabular-nums">
                        #{c.id}
                      </span>{' '}
                      {c.name}
                    </span>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-6 px-2 text-[11px]"
                      onClick={() => {
                        onAttach(campaignAsOfferItem(c));
                        close();
                      }}
                    >
                      Attach
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </DrawerBody>
        <DrawerFooter>
          <Button type="button" variant="outline" size="sm" onClick={close}>
            Close
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default BinomCampaignAsOfferPicker;
